import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login: authLogin, user, loading: authLoading } = useAuth();

  // If already authenticated as admin, redirect immediately
  useEffect(() => {
    if (!authLoading && user) {
      const roles = user.roles || (user.role ? [user.role] : []);
      if (roles.includes('admin')) {
        navigate('/admin/dashboard', { replace: true });
      }
    }
  }, [user, authLoading, navigate]);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authLogin({
        email: formData.email,
        password: formData.password
      });

      const isAdmin = response?.data?.user?.roles && Array.isArray(response.data.user.roles) && response.data.user.roles.includes('admin');

      if (response.success && isAdmin) {
        navigate('/admin/dashboard');
      } else if (!isAdmin) {
        setError('Invalid credentials. Admin access required.');
      } else {
        setError(response.message || 'Login failed');
      }
    } catch (err: any) {
      console.error('❌ Login error:', err);
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Helmet>
        <title>Admin Login | Virewire</title>
        <meta name="description" content="Admin Portal - Login to manage your platform" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">Virewire</h1>
            <p className="text-gray-400">Admin Portal</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Admin Login</CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="admin@virewire.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-10" 
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <a href="/admin/register" className="text-sm text-blue-600 hover:text-blue-700">
                  Don't have an admin account? Register
                </a>
              </div>
            </CardContent>
          </Card>

          <div className="mt-8 text-center text-sm text-gray-400">
            <p>© 2024 Virewire. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminLoginPage;
